import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { createTag } from '@services/queries/tags';
import { indexTagsCategory } from '@services/queries/tagsCategory';

import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { checkDevice } from '@store/redux/features/mobile/mobileSelectors';
import { DUPLICATE_TAG_ERROR_MESSAGE } from '@pages/ConfigurationPage/Components/TagsManagement/Components/AddNewMenu/Components/AddTagDialog/AddTagDialog.utils';
import { AxiosError } from 'axios';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  Input,
  Textarea,
  Error,
  LineDivider,
} from './AddTagDialog.styles';

interface IAddTagDialog {
  open: boolean;
  onClose: () => void;
}

interface IFormData {
  name: string;
  description: string;
  tagCategoryId: string;
}

export const AddTagDialog = ({ open, onClose }: IAddTagDialog) => {
  const portalDiv = document.getElementById('portal');
  const history = useHistory();

  const queryClient = useQueryClient();

  const { data } = useQuery(['tagsCategory'], () => indexTagsCategory());

  const Options = data?.map(option => {
    return { value: option.id, label: `${option.name}` };
  });

  const schema = yup.object().shape({
    name: yup
      .string()
      .max(24, 'Tag name must be under the 24 character limit')
      .required(),
    description: yup.string(),
    tagCategoryId: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { isValid, errors },
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const mutationCreateTag = useMutation(createTag, {
    onSuccess: () => {
      queryClient.invalidateQueries('tags');
      queryClient.invalidateQueries('tagCategories');
      onClose();
      setValue('name', '');
      setValue('description', '');
      setValue('tagCategoryId', '');
    },
    onError: (error: AxiosError) => {
      if (error.response?.data?.message === DUPLICATE_TAG_ERROR_MESSAGE) {
        setError('name', {
          type: 'validate',
          message: DUPLICATE_TAG_ERROR_MESSAGE,
        });
      }
    },
  });

  const onSubmit = (Tag: IFormData) => {
    if (!mutationCreateTag.isLoading) {
      if (data)
        mutationCreateTag.mutate({
          name: Tag.name,
          description: Tag.description,
          tagCategoryId: Tag.tagCategoryId,
        });
    }
  };
  let showErrMsg = false;
  let isValidNameErr = false;
  if (errors.name) {
    isValidNameErr =
      errors.name.type === 'max' || errors.name.type === 'validate';
    showErrMsg = Boolean(isValidNameErr && errors.name?.message);
  }

  const { isDesktop, isMobile } = useSelector(checkDevice());
  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Create Tag</Title>
              <Close
                onClick={() => {
                  onClose();
                  clearErrors('name');
                }}
              >
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label className="mandatory">Category</Label>

              <Controller
                control={control}
                name="tagCategoryId"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={Options}
                    placeholder="Select a Category"
                    value={Options?.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                    }}
                  />
                )}
              />

              {isDesktop && (
                <>
                  <Label className="tip">Need a different category?</Label>
                  <Label
                    className="tipBlue"
                    onClick={() => history.push('/configuration?tab=tags')}
                  >
                    Go to Tag Management page and add a new category.
                  </Label>
                  <Divider height="24px" />
                  <LineDivider />
                </>
              )}
              {isMobile && <Divider height="24px" />}

              <Label className="mandatory">Tag name</Label>

              <Input
                placeholder="Ex. Studio"
                {...register('name')}
                isError={showErrMsg}
              />
              {showErrMsg && <Error>{errors?.name?.message}</Error>}

              <Divider height="16px" />

              <Label className="optional">Description (optional)</Label>

              <Textarea
                placeholder="Description"
                {...register('description')}
              />

              <Line>
                <Confirm
                  type="submit"
                  disabled={!isValid || mutationCreateTag.isLoading}
                >
                  Confirm
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
